import React, { useCallback, useEffect, useState } from 'react';
import { Phone, MapPin, Globe, Mail, Clock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
type PrintMode = 'none' | 'front' | 'back';
/*
 * Vistaprint UK specs:
 * Trim:  85 × 55 mm
 * Bleed: 91 × 61 mm (3 mm each side)
 * Safe:  79 × 49 mm (3 mm inside trim)
 *
 * Screen preview uses a scale factor to show the card at a comfortable size.
 * Print output uses exact mm dimensions.
 */
const SCALE = 5.5; // 1mm ≈ 5.5px on screen
const BLEED_W = 91 * SCALE; // 500.5 → 501px
const BLEED_H = 61 * SCALE; // 335.5 → 336px
const TRIM_INSET = 3 * SCALE; // 16.5px
const SAFE_INSET = 6 * SCALE; // 33px (3mm bleed + 3mm safe)
export function BusinessCardPage() {
  const [printMode, setPrintMode] = useState<PrintMode>('none');
  const [showGuides, setShowGuides] = useState(true);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode !== 'none') {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode('none');
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  return <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center print:bg-white print:p-0 print:m-0" data-id="element-911" data-mp="amdy83znb56k">
      <div className="max-w-7xl w-full space-y-10 print:space-y-0" data-id="element-912" data-mp="1koab84z2odj">
        {/* Page title */}
        <div className="text-center print:hidden" data-id="element-913" data-mp="y8kl1wdqapgu">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-914" data-mp="taiz93ox4c1o">
            Dentico Clinic Business Card
          </h1>
          <p className="mt-2 text-gray-600" data-id="element-915" data-mp="ojkf1t0t6tt5">
            Vistaprint-ready · 91 × 61 mm (with 3 mm bleed)
          </p>
          <div className="mt-4 flex items-center justify-center gap-3" data-id="element-916" data-mp="kj0hwpg8jq70">
            <button onClick={() => setShowGuides(!showGuides)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-917" data-mp="1gzv7o1cf8g2x">
              {showGuides ? <Eye className="w-3.5 h-3.5" data-id="element-918" data-mp="3bgv8bllwhokc" /> : <EyeOff className="w-3.5 h-3.5" data-id="element-919" data-mp="3bieshnaogjre" />}
              {showGuides ? 'Guides On' : 'Guides Off'}
            </button>
            <div className="flex items-center gap-3 text-[10px] text-gray-400" data-id="element-920" data-mp="31pquip7yo9r3">
              <span className="flex items-center gap-1" data-id="element-921" data-mp="2nr0xf9l56a2q">
                <span className="w-3 h-0 border-t border-dashed border-red-400 inline-block" data-id="element-922" data-mp="2npg1bfj6ij2r" />{' '}
                Trim
              </span>
              <span className="flex items-center gap-1" data-id="element-923" data-mp="2dstjz0q2aa5h">
                <span className="w-3 h-0 border-t border-dashed border-blue-400 inline-block" data-id="element-924" data-mp="2duedqo4jxp2a" />{' '}
                Safe zone
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-20 print:block" data-id="element-925" data-mp="c1q682kvkfgp">
          {/* ===== FRONT SIDE ===== */}
          <div className="flex flex-col items-center gap-3 print:mb-0" data-card="front" style={printMode === 'back' ? {
          display: 'none'
        } : undefined} data-id="element-926" data-mp="21i2h14hv3d5h">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden" data-id="element-927" data-mp="26gx087jqr3q4">
              Front Side
            </span>
            <div className="card-bleed relative bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-200 print:shadow-none print:rounded-none print:border-0" style={{
            width: BLEED_W,
            height: BLEED_H
          }} data-id="element-928" data-mp="2k78x0jr9tqnl">
              {/* Bleed-area accent bars — extend full width */}
              <div className="absolute top-0 left-0 w-full" style={{
              height: TRIM_INSET + 6,
              backgroundColor: '#0d9488'
            }} data-id="element-929" data-mp="zs6ymu6c1szf" />
              <div className="absolute bottom-0 left-0 w-full" style={{
              height: TRIM_INSET + 6,
              backgroundColor: '#0d9488'
            }} data-id="element-930" data-mp="waj4gsxu2knq" />

              {/* Safe-zone content */}
              <div className="absolute flex flex-col items-center justify-center" style={{
              top: SAFE_INSET,
              left: SAFE_INSET,
              right: SAFE_INSET,
              bottom: SAFE_INSET
            }} data-id="element-931" data-mp="1b8lb5c05vm97">
                {/* Logo */}
                <div className="mb-5" data-id="element-932" data-mp="nh61sz9yw4og">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: '#0d9488'
                }} data-id="element-933" data-mp="s9h76hnh8ioe">
                    <span className="text-white font-bold text-4xl" data-id="element-934" data-mp="3vxit8avj74a1">D</span>
                  </div>
                </div>

                {/* Brand */}
                <h2 className="text-3xl font-bold text-gray-900 tracking-tight" data-id="element-935" data-mp="2d13hh8x4nd48">
                  Dentico
                  <span style={{
                  color: '#0d9488'
                }} data-id="element-936" data-mp="242rjumxuff2a">
                    Clinic
                  </span>
                </h2>
                <div className="w-10 h-px mx-auto my-3 rounded-full" style={{
                backgroundColor: '#0d9488',
                opacity: 0.7
              }} data-id="element-937" data-mp="1u4bryhqzs1rq" />
                <p className="text-gray-500 font-medium text-xs tracking-[0.06em] uppercase text-center" data-id="element-938" data-mp="1ldbenmb7qyfj">
                  Українська стоматологічна клініка в Лондоні
                </p>
              </div>

              {/* Guides overlay */}
              {showGuides && <GuidesOverlay data-id="element-939" data-mp="16ezo7xefolwt" />}
            </div>
          </div>

          {/* ===== BACK SIDE ===== */}
          <div className="flex flex-col items-center gap-3 print:mb-0" data-card="back" style={printMode === 'front' ? {
          display: 'none'
        } : undefined} data-id="element-940" data-mp="wrn6w303yrmo">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider print:hidden" data-id="element-941" data-mp="rs0t9sgdb9dc">
              Back Side
            </span>
            <div className="card-bleed relative bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-200 print:shadow-none print:rounded-none print:border-0" style={{
            width: BLEED_W,
            height: BLEED_H
          }} data-id="element-942" data-mp="1fierk0pjq4gd">
              {/* Safe-zone content */}
              <div className="absolute flex flex-col" style={{
              top: SAFE_INSET,
              left: SAFE_INSET,
              right: SAFE_INSET,
              bottom: SAFE_INSET
            }} data-id="element-943" data-mp="1ygqxzep2p089">
                {/* Header */}
                <div className="flex justify-between items-center border-b border-gray-100 pb-2.5 mb-3" data-id="element-944" data-mp="3iz1m7lpfdggt">
                  <div data-id="element-945" data-mp="1itczmfsvnm8">
                    <h3 className="text-base font-bold text-gray-900 leading-tight" data-id="element-946" data-mp="1gysuaccrzj7">
                      Dentico
                      <span style={{
                      color: '#0d9488'
                    }} data-id="element-947" data-mp="3sp34txz0e6oc">
                        Clinic
                      </span>
                    </h3>
                    <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-948" data-mp="3ex6fu2sn0cw6">
                      Українська стоматологічна клініка в Лондоні
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{
                  backgroundColor: '#0d9488'
                }} data-id="element-949" data-mp="3a0g57uzlq9i5">
                    <span className="text-white font-bold text-sm" data-id="element-950" data-mp="2ir3gs7uq02mv">D</span>
                  </div>
                </div>

                {/* Contacts + QR */}
                <div className="flex-1 flex justify-between gap-4" data-id="element-951" data-mp="2nnlj712kvow1">
                  <div className="flex flex-col gap-[9px] justify-center" data-id="element-952" data-mp="2snzgpp6jzgft">
                    <div className="flex items-center gap-2.5" data-id="element-953" data-mp="2xfzizoa3ilfk">
                      <Phone className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-954" data-mp="2xharu2ieejdj" />
                      <span className="text-[13px] font-semibold text-gray-800" data-id="element-955" data-mp="3bfihrqh0okf7">
                        +44 7345 197820
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-956" data-mp="3gc96t3asuqcr">
                      <Globe className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-957" data-mp="1h2j7mcveoifb" />
                      <span className="text-[12px] font-medium text-gray-700" data-id="element-958" data-mp="1eicrl6tip1ie">
                        denticoclinic.co.uk
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-959" data-mp="14s1mvi20eoag">
                      <Mail className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-960" data-mp="zs5jnl14z0vy" />
                      <span className="text-[12px] font-semibold text-gray-800" data-id="element-961" data-mp="1sg1fyomsmqs7">
                        info@denticoclinic.co.uk
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-962" data-mp="1iina1iyhxke5">
                      <MapPin className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-963" data-mp="1ii5763hbw7x9" />
                      <span className="text-[11px]" style={{
                      color: '#374151'
                    }} data-id="element-964" data-mp="75ku8q480yv1">
                        360–364 City Rd, London EC1V 2PY
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5" data-id="element-965" data-mp="1kkmqgm2movpy">
                      <Clock className="w-3.5 h-3.5 shrink-0" style={{
                      color: '#0d9488'
                    }} data-id="element-966" data-mp="1kjbrr0o1ie5p" />
                      <span className="text-[11px]" style={{
                      color: '#374151'
                    }} data-id="element-967" data-mp="1ucivyi71no47">
                        Mon–Sat 10:00–18:00
                      </span>
                    </div>
                  </div>

                  {/* QR */}
                  <div className="flex flex-col items-center justify-center" data-id="element-968" data-mp="8y4wkdknxwuj">
                    <div className="w-[72px] h-[72px] rounded overflow-hidden" data-id="element-969" data-mp="2kjx2h01pc9j3">
                      <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://wa.me/447345197820" alt="WhatsApp QR code" className="w-full h-full object-contain" data-id="element-970" data-mp="2pjjnhvnruu2g" />
                    </div>
                    <span className="text-[7px] mt-1 uppercase tracking-wider" style={{
                    color: '#6b7280'
                  }} data-id="element-971" data-mp="21u8j8477qhjk">
                      WhatsApp Me
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-2 border-t border-gray-100 mt-auto" data-id="element-972" data-mp="1wv4blhzxsrvc">
                  <p className="text-[9px] leading-relaxed" style={{
                  color: '#6b7280'
                }} data-id="element-973" data-mp="3mavbriajzw52">
                    Appointments available online or by phone.
                  </p>
                </div>
              </div>

              {/* Guides overlay */}
              {showGuides && <GuidesOverlay data-id="element-974" data-mp="3ftah1531og19" />}
            </div>
          </div>
        </div>

        {/* Buttons + specs */}
        <div className="flex flex-col items-center gap-5 pt-6 print:hidden" data-id="element-975" data-mp="3bsqh5tr36ybf">
          <div className="flex gap-4" data-id="element-976" data-mp="cctr9m2pgvs2">
            <Button onClick={() => handlePrint('front')} size="lg" className="shadow-lg hover:shadow-xl transition-all" data-id="element-977" data-mp="cbiewxv72fpc">
              Save Front as PDF
            </Button>
            <Button onClick={() => handlePrint('back')} size="lg" className="shadow-lg hover:shadow-xl transition-all" data-id="element-978" data-mp="2kxjfjathran">
              Save Back as PDF
            </Button>
          </div>

          {/* Vistaprint spec reminder */}
          <div className="bg-white border border-gray-200 rounded-lg px-6 py-4 max-w-md text-center shadow-sm" data-id="element-979" data-mp="qb1pdxbha34t">
            <p className="text-xs font-semibold text-gray-700 mb-2" data-id="element-980" data-mp="291fkqejwv1zf">
              Vistaprint Print Settings
            </p>
            <div className="text-[11px] text-gray-500 space-y-1 leading-relaxed" data-id="element-981" data-mp="2i01060u360vz">
              <p data-id="element-982" data-mp="2i2dqb19zzhk4">
                In the print dialog, select <strong data-id="element-983" data-mp="2mzduf6naea8l">"Save as PDF"</strong>
              </p>
              <p data-id="element-984" data-mp="2ry8esd0wk6ut">
                Paper size: <strong data-id="element-985" data-mp="2rz08fiaxvuig">Custom — 91 × 61 mm</strong>
              </p>
              <p data-id="element-986" data-mp="2wxbi9t18i50o">
                Margins: <strong data-id="element-987" data-mp="2wwi80i32gxds">None</strong>
              </p>
              <p data-id="element-988" data-mp="2wv78wvkkgdt0">
                Scale: <strong data-id="element-989" data-mp="31oa4tactfzcd">100%</strong>
              </p>
              <p className="pt-1 text-gray-400" data-id="element-990" data-mp="31otl6mmtifoa">
                Upload both PDFs to Vistaprint as front & back
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles — exact Vistaprint dimensions */}
      <style data-id="element-991" data-mp="3fnjgbwq2u5a0">{`
        @media print {
          @page {
            size: 91mm 61mm;
            margin: 0;
          }

          /* Force color printing */
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Page background */
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }

          /* Hide everything except the card */
          h1, button, nav, header, footer,
          .print\\:hidden, [class*="print:hidden"] {
            display: none !important;
          }

          /* Strip layout from wrapper divs (not card internals) */
          .min-h-screen {
            display: block !important;
            min-height: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
          }

          .max-w-7xl {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          /* The card — keep pixel size, scale to fit 91×61mm */
          .card-bleed {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: ${BLEED_W}px !important;
            height: ${BLEED_H}px !important;
            border-radius: 0 !important;
            border: none !important;
            box-shadow: none !important;
            overflow: hidden !important;
            transform-origin: top left !important;
            transform: scale(calc(91mm / ${BLEED_W}px), calc(61mm / ${BLEED_H}px)) !important;
          }

          /* Hide guide overlays */
          .card-bleed .pointer-events-none {
            display: none !important;
          }
        }
      `}</style>
    </div>;
}
/* Trim + safe zone guide lines (screen only) */
function GuidesOverlay() {
  return <div className="absolute inset-0 pointer-events-none print:hidden" data-id="element-992" data-mp="1hb6zto6zbrb8">
      {/* Trim line (red dashed) — 3mm from edge */}
      <div className="absolute border border-dashed" style={{
      top: TRIM_INSET,
      left: TRIM_INSET,
      right: TRIM_INSET,
      bottom: TRIM_INSET,
      borderColor: 'rgba(239, 68, 68, 0.45)'
    }} data-id="element-993" data-mp="1r5cjsnizgza6" />
      {/* Safe zone (blue dashed) — 6mm from edge */}
      <div className="absolute border border-dashed" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET,
      borderColor: 'rgba(59, 130, 246, 0.35)'
    }} data-id="element-994" data-mp="1d6cxhmiy0wh6" />
    </div>;
}